import { useState } from 'react';
import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Check, Copy, Link2, ShoppingBasket, Trash2, X } from 'lucide-react';
import { Button, CenteredSpinner, Chip, ErrorState } from '@/components/ui';
import { KIND_ICON, KIND_TINT, cn } from '@/lib/format';
import { t } from '@/lib/i18n';
import {
  useAddToBasket,
  useBasket,
  useClearBasket,
  useCreateShare,
  useRemoveFromBasket,
  useRevokeShare,
  useShares,
} from '@/lib/queries';
import type { BasketItem } from '@/lib/api';

/**
 * The share basket — parity with the old Pimcore basket.
 *
 * Collect assets, set a per-asset size, then mint a link that hands them to
 * someone outside the DAM. Two deliberate improvements on the original: the
 * basket is server-side (the old one lived in localStorage and died with the
 * browser cache), and every link must carry an expiry.
 */
export function BasketPage() {
  const { data: items, isPending, error, refetch } = useBasket();
  const clear = useClearBasket();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  if (isPending) return <CenteredSpinner label={t('basketPage.loadingBasket')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const basket = items ?? [];

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-5">
        {/* People usually arrive from an asset page ("Go to basket") and want
            to return to it after making a link, so Back is history, as on the
            asset page. Nothing to go back to (a pasted link, a new tab): no button. */}
        {canGoBack && (
          <button
            onClick={() => router.history.back()}
            className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted hover:text-strong"
          >
            <ArrowLeft className="size-3.5" /> {t('basketPage.back')}
          </button>
        )}
        <header className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-strong">
              <ShoppingBasket className="size-5 text-brand-500" /> {t('basketPage.shareBasket')}
            </h1>
            <p className="mt-0.5 text-xs text-muted">
              {basket.length === 0
                ? t('basketPage.emptyBasketHint')
                : basket.length === 1
                  ? t('basketPage.oneItemReady', { n: basket.length })
                  : t('basketPage.nItemsReady', { n: basket.length })}
            </p>
          </div>
          {basket.length > 0 && (
            <Button
              variant="secondary"
              className="h-8 text-xs"
              loading={clear.isPending}
              onClick={() => clear.mutate()}
            >
              <Trash2 className="size-3.5" /> {t('basketPage.emptyBasketButton')}
            </Button>
          )}
        </header>

        {basket.length > 0 && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {basket.map((item) => (
                <BasketCard key={item.assetId} item={item} />
              ))}
            </div>
            <CreateShare />
          </>
        )}

        <ExistingShares />
      </div>
    </div>
  );
}

/**
 * One basket entry, with its size control.
 *
 * Size is a percentage, exactly as the old basket stored it — 100 means the
 * original bytes are shipped untouched rather than re-encoded.
 */
function BasketCard({ item }: { item: BasketItem }) {
  const remove = useRemoveFromBasket();
  const update = useAddToBasket();
  const Icon = KIND_ICON[item.kind];

  const scaled = (n: number | null) => (n ? Math.round((n * item.size) / 100) : null);
  const w = scaled(item.width);
  const h = scaled(item.height);

  return (
    <div className="relative rounded-[var(--radius-card)] surface-card border line p-3">
      <button
        type="button"
        aria-label={t('basketPage.removeAria', { fileName: item.fileName })}
        title={t('basketPage.removeFromBasket')}
        className="absolute right-2 top-2 rounded p-1 text-faint transition-colors hover:bg-[var(--surface-sunken)] hover:text-red-600"
        onClick={() => remove.mutate(item.assetId)}
      >
        <X className="size-3.5" />
      </button>

      <Link
        to="/assets/$assetId"
        params={{ assetId: item.assetId }}
        className="flex items-center gap-3"
      >
        <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded surface-sunken">
          {item.thumbUrl ? (
            <img src={item.thumbUrl} alt="" className="size-full object-cover" />
          ) : (
            <Icon className={cn('size-6', KIND_TINT[item.kind])} />
          )}
        </div>
        <div className="min-w-0 pr-5">
          <p className="truncate text-sm text-body" title={item.fileName}>
            {item.fileName}
          </p>
          {w && h ? (
            <p className="text-[11px] text-faint">
              {w}×{h}
              {item.size !== 100 && <span className="text-brand-500"> ({item.size}%)</span>}
            </p>
          ) : (
            <p className="text-[11px] text-faint">{t(`assetKind.${item.kind}`)}</p>
          )}
        </div>
      </Link>

      {/* Sizing only applies to images; other kinds always ship as-is. */}
      {item.kind === 'image' && (
        <label className="mt-3 block">
          <span className="mb-1 flex items-center justify-between text-[11px] text-faint">
            <span>{t('basketPage.size')}</span>
            <span>{item.size === 100 ? t('basketPage.original') : `${item.size}%`}</span>
          </span>
          <input
            type="range"
            min={10}
            max={100}
            step={10}
            value={item.size}
            className="w-full accent-[var(--brand-500)]"
            onChange={(e) =>
              update.mutate({
                assetId: item.assetId,
                size: Number(e.target.value),
                quality: item.quality,
                dpi: item.dpi,
              })
            }
          />
        </label>
      )}
    </div>
  );
}

const EXPIRY_CHOICES = [1, 3, 7, 14, 30, 90];

function CreateShare() {
  const create = useCreateShare();
  const [expiryDays, setExpiryDays] = useState(7);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState<string | null>(null);

  return (
    <div className="mt-5 rounded-[var(--radius-card)] surface-card border line p-4">
      <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        <Link2 className="size-3.5 text-brand-500" /> {t('basketPage.createShareLink')}
      </h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[11px] text-faint">{t('basketPage.descriptionOptional')}</span>
          <input
            className={FIELD}
            value={title}
            maxLength={200}
            placeholder={t('basketPage.descriptionPlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] text-faint">{t('basketPage.expiresAfter')}</span>
          <select
            className={FIELD}
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value))}
          >
            {EXPIRY_CHOICES.map((d) => (
              <option key={d} value={d}>
                {d === 1 ? t('basketPage.oneDay', { d }) : t('basketPage.nDays', { d })}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="mt-2 text-[11px] text-faint">
        {t('basketPage.anyoneWithLink')}
      </p>

      {create.error && <p className="mt-2 text-xs text-red-600">{(create.error as Error).message}</p>}

      <Button
        variant="primary"
        className="mt-3 h-8 text-xs"
        loading={create.isPending}
        onClick={() =>
          create.mutate(
            { expiryDays, title: title.trim() || null },
            { onSuccess: (r) => setLink(r.url) },
          )
        }
      >
        <Link2 className="size-3.5" /> {t('basketPage.createLink')}
      </Button>

      {link && <CopyableLink url={link} />}
    </div>
  );
}

/**
 * The freshly-minted link. Shown once and prominently: tokens are hashed at
 * rest, so this is the only moment the full URL can be displayed.
 */
function CopyableLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 rounded-md border border-brand-500 bg-[var(--surface-sunken)] p-3">
      <p className="mb-1.5 text-[11px] font-medium text-brand-500">
        {t('basketPage.linkCreated')}
      </p>
      <div className="flex items-start gap-1.5">
        <code className="min-w-0 flex-1 break-all text-xs text-body">{url}</code>
        <button
          type="button"
          aria-label={copied ? t('basketPage.linkCopied') : t('basketPage.copyLink')}
          title={copied ? t('basketPage.copied') : t('basketPage.copyLink')}
          className="shrink-0 rounded p-0.5 text-faint transition-colors hover:text-strong"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? <Check className="size-4 text-brand-500" /> : <Copy className="size-4" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Previously created links, with revoke.
 *
 * The old bundle had list and delete endpoints but nothing in the basket UI
 * reached them, so a link once created could not be taken back.
 */
function ExistingShares() {
  const { data: shares } = useShares();
  const revoke = useRevokeShare();

  if (!shares?.length) return null;

  return (
    <div className="mt-6">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{t('basketPage.yourShareLinks')}</h2>
      <div className="overflow-hidden rounded-[var(--radius-card)] border line">
        <table className="w-full text-sm">
          <thead className="surface-sunken text-[11px] uppercase tracking-wide text-faint">
            <tr>
              <th className="px-3 py-2 text-left font-medium">{t('basketPage.description')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('basketPage.items')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('basketPage.expires')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('basketPage.activity')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {shares.map((s) => (
              <tr key={s.id} className="border-t line">
                <td className="px-3 py-2 text-body">{s.title ?? <span className="text-faint">{t('basketPage.untitled')}</span>}</td>
                <td className="px-3 py-2 text-muted">{s.itemCount}</td>
                <td className="px-3 py-2">
                  {s.revokedAt ? (
                    <Chip>{t('basketPage.revoked')}</Chip>
                  ) : s.expired ? (
                    <Chip>{t('basketPage.expired')}</Chip>
                  ) : (
                    <span className="text-muted">{new Date(s.expiresAt).toLocaleDateString()}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] text-faint">
                  {s.viewCount === 1 ? t('basketPage.oneView', { n: s.viewCount }) : t('basketPage.nViews', { n: s.viewCount })} · {s.downloadCount === 1 ? t('basketPage.oneDownload', { n: s.downloadCount }) : t('basketPage.nDownloads', { n: s.downloadCount })}
                </td>
                <td className="px-3 py-2 text-right">
                  {s.active && (
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => revoke.mutate(s.id)}
                    >
                      {t('basketPage.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FIELD =
  'w-full rounded-md border border-line bg-[var(--surface-card)] px-2 py-1.5 text-sm text-body outline-none focus:border-brand-500';
